import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Badge, Button, Card, Group, NumberInput, Paper, Select, Stack, Switch, Text } from "@mantine/core";
import { BellRing, Play } from "lucide-react";
import { toast } from "sonner";
import { BooleanBadge } from "@nodus/ui";
import { doorbellChimeApi, type ChimeField, type ChimeSiren, type ChimeSirens, type ChimeValues } from "@/lib/api/doorbell-chime";
import { DOORBELL_CHIME_KEY, useDoorbellChime } from "@/hooks/use-doorbell-chime";
import {
  DEFAULT_CHIME_SECONDS,
  PLAY_COOLDOWN_MS,
  chimeChanged,
  chimeError,
  chimePayload,
  fieldOf,
  sirenKey,
} from "@/lib/doorbell-chime";

interface SirenRowProps {
  siren: ChimeSiren;
  fields: ChimeField[];
  saving: boolean;
  playing: boolean;
  /** The last test play of this siren is less than 10 s ago. */
  coolingDown: boolean;
  onSave: (values: ChimeValues) => void;
  onPlay: (values: ChimeValues) => void;
}

/** One siren: chime on/off, its own sounds and volumes, the length, Play and Save. */
export function ChimeSirenRow({ siren, fields, saving, playing, coolingDown, onSave, onPlay }: SirenRowProps) {
  const [draft, setDraft] = useState<ChimeValues>(siren.settings);
  useEffect(() => setDraft(siren.settings), [siren.settings]);
  const set = (patch: ChimeValues) => setDraft((d) => ({ ...d, ...patch }));

  const sounds = siren.choices.doorbell_sound ?? [];
  const volumes = siren.choices.doorbell_volume ?? [];
  const seconds = fieldOf(fields, "doorbell_seconds");
  const changed = chimeChanged(siren.settings, draft);
  const label = (key: ChimeField["key"], fallback: string) => fieldOf(fields, key)?.label ?? fallback;

  return (
    <Paper withBorder radius="md" p="md" data-testid="chime-siren">
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
          <Stack gap={0} miw={0}>
            <Text fw={600} truncate>{siren.room}</Text>
            <Text fz="xs" c="dimmed" truncate>
              {[siren.floor, siren.name].filter(Boolean).join(" · ")}
            </Text>
          </Stack>
          <Group gap={6} wrap="nowrap">
            {siren.sounding && <Badge color="orange" variant="light" size="sm">Sounding</Badge>}
            <BooleanBadge value={siren.available} labels={["Online", "Offline"]} size="sm" />
          </Group>
        </Group>

        <Switch
          label={label("doorbell_chime", "Chime on doorbell rings")}
          checked={draft.doorbell_chime === true}
          disabled={saving}
          onChange={(e) => set({ doorbell_chime: e.currentTarget.checked })}
        />

        <Group gap="sm" align="flex-start" grow preventGrowOverflow={false} wrap="wrap">
          {sounds.length > 0 && (
            <Select
              label="Sound"
              placeholder="The siren's own sound"
              data={sounds}
              value={draft.doorbell_sound ?? null}
              onChange={(v) => set({ doorbell_sound: v ?? undefined })}
              clearable
              allowDeselect
              disabled={saving}
              miw={140}
              comboboxProps={{ withinPortal: true }}
            />
          )}
          {volumes.length > 0 && (
            <Select
              label="Volume"
              placeholder="The siren's own volume"
              data={volumes}
              value={draft.doorbell_volume ?? null}
              onChange={(v) => set({ doorbell_volume: v ?? undefined })}
              clearable
              allowDeselect
              disabled={saving}
              miw={140}
              comboboxProps={{ withinPortal: true }}
            />
          )}
          <NumberInput
            label="Length"
            suffix=" s"
            min={seconds?.min ?? 1}
            max={seconds?.max ?? 30}
            allowDecimal={false}
            clampBehavior="strict"
            placeholder={`${seconds?.default ?? DEFAULT_CHIME_SECONDS} s`}
            value={draft.doorbell_seconds ?? ""}
            onChange={(v) => set({ doorbell_seconds: typeof v === "number" ? v : undefined })}
            disabled={saving}
            miw={110}
          />
        </Group>

        <Group justify="flex-end" gap="sm">
          <Button
            variant="default"
            leftSection={<Play size={14} />}
            loading={playing}
            disabled={!siren.available || siren.sounding || coolingDown}
            onClick={() => onPlay(chimePayload(draft))}
          >
            {coolingDown ? "Played" : "Play"}
          </Button>
          <Button loading={saving} disabled={!changed} onClick={() => onSave(chimePayload(draft))}>
            Save
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
}

interface ViewProps {
  data: ChimeSirens;
  savingKey: string | null;
  playingKey: string | null;
  cooling: Record<string, boolean>;
  onSave: (siren: ChimeSiren, values: ChimeValues) => void;
  onPlay: (siren: ChimeSiren, values: ChimeValues) => void;
  /** Only the sirens, without the card's title and intro (the guide step already says it). */
  bare?: boolean;
}

/** The company's doorbell chime — presentational. */
export function DoorbellChimeView({ data, savingKey, playingKey, cooling, onSave, onPlay, bare = false }: ViewProps) {
  const rows = (
    <Stack gap="sm" data-testid={bare ? "doorbell-chime" : undefined}>
      {data.sirens.map((s) => {
          const key = sirenKey(s);
          return (
            <ChimeSirenRow
              key={key}
              siren={s}
              fields={data.fields}
              saving={savingKey === key}
              playing={playingKey === key}
              coolingDown={cooling[key] === true}
              onSave={(values) => onSave(s, values)}
              onPlay={(values) => onPlay(s, values)}
            />
          );
        })}
    </Stack>
  );
  if (bare) return rows;
  return (
    <Card withBorder padding="lg" data-testid="doorbell-chime">
      <Stack gap="sm">
        <Group gap="xs" wrap="nowrap">
          <BellRing size={18} />
          <Text fw={500}>Doorbell sound</Text>
        </Group>
        <Text fz="sm" c="dimmed">
          When a visitor rings for your company, the siren in your office plays a short sound. Choose the sound, how
          loud and how long. Press Play to hear it.
        </Text>
        {rows}
      </Stack>
    </Card>
  );
}

/**
 * The company admin's doorbell chime card (Settings page, and the "Doorbell
 * sound" guide's block). Lists only the sirens in rooms the company uses
 * alone — the rooms a ring chimes in. Renders nothing for a member (the API
 * answers 403) or a company without a siren. `onUsed` fires after a Save or a
 * Play: the guide ticks its step with it.
 */
export function DoorbellChimeCard({
  onUsed,
  empty = null,
  bare = false,
}: { onUsed?: () => void; empty?: React.ReactNode; bare?: boolean } = {}) {
  const qc = useQueryClient();
  const { data, isLoading, isError } = useDoorbellChime();
  const [cooling, setCooling] = useState<Record<string, boolean>>({});

  const save = useMutation({
    mutationFn: ({ siren, values }: { siren: ChimeSiren; values: ChimeValues }) =>
      doorbellChimeApi.save(siren, values).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: DOORBELL_CHIME_KEY });
      toast.success("Doorbell sound saved");
      onUsed?.();
    },
    onError: (err) => toast.error(chimeError(err, "Couldn't save the doorbell sound. Please try again.")),
  });
  const play = useMutation({
    mutationFn: ({ siren, values }: { siren: ChimeSiren; values: ChimeValues }) =>
      doorbellChimeApi.play(siren, values).then((r) => r.data),
    onMutate: ({ siren }) => {
      const key = sirenKey(siren);
      setCooling((c) => ({ ...c, [key]: true }));
      setTimeout(() => setCooling((c) => ({ ...c, [key]: false })), PLAY_COOLDOWN_MS);
    },
    onSuccess: () => onUsed?.(),
    onError: (err) => toast.error(chimeError(err, "Couldn't play the sound. Please try again.")),
  });

  if (isLoading || isError || !data) return null;
  if (data.sirens.length === 0) return <>{empty}</>;
  return (
    <DoorbellChimeView
      data={data}
      savingKey={save.isPending && save.variables ? sirenKey(save.variables.siren) : null}
      playingKey={play.isPending && play.variables ? sirenKey(play.variables.siren) : null}
      cooling={cooling}
      bare={bare}
      onSave={(siren, values) => save.mutate({ siren, values })}
      onPlay={(siren, values) => play.mutate({ siren, values })}
    />
  );
}
